import {
  abs,
  atan,
  clamp,
  dot,
  Fn,
  float,
  floor,
  int,
  ivec2,
  max,
  mix,
  normalize,
  positionGeometry,
  screenUV,
  select,
  texture,
  textureLoad,
  uniform,
  uniformArray,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';
import * as THREE from 'three/webgpu';
import type { CloudLayer } from '../atmosphere/CloudLayer';
import type { LightRig } from '../lighting/LightRig';
import { octDecode, octEncode } from '../render/octahedral';
import { FACE_N, FACE_U, FACE_V } from './cubeSphere';
import { paletteHeight, type TerrainProfile } from './TerrainProfile';
import type { TerrainQuadtree } from './TerrainQuadtree';
import type { SurfaceContext, TerrainStyle } from './TerrainStyle';
import { TILE_RES, type TileGenerator } from './TileGenerator';

type N = any;

/** Texels per side of the octahedral map. */
const SIZE = 256;
/** Mip levels the map keeps (blurrier reflections read the coarser ones). */
const LEVELS = 5;
/** The map is redrawn a band at a time, one band a frame. */
const BANDS = 4;

/**
 * A sphere hollow's wall as seen from its centre, in an octahedral map of linear radiance: its
 * ground (the palette over the six root tiles, which always stay loaded) in the rig's light,
 * cloud shadows and all, with the cloud deck's sunlit tops over it. Lakes reflect it: a ray
 * reflected off the water meets the far wall, and the map says what is there. A quarter of it
 * is redrawn every frame (about 0.07 ms), so it follows the day and the drifting clouds.
 * Its alpha is the light the wall gives off itself (luminance): where the lava is, and how hard
 * it is flaring (LightRig.setLocalGlow).
 */
export class WorldPanorama {
  readonly target: THREE.RenderTarget;
  /** 1 once the map has been drawn (water falls back to its flat sky colour until then). */
  readonly ready = uniform(0);
  /** The sky colour the terrain's water reflects (the scene keeps the two the same). */
  readonly waterSky = uniform(new THREE.Color(0.12, 0.16, 0.24));
  /** Seconds since start, for the palettes' moving surfaces. */
  readonly time = uniform(0);
  private readonly mesh: THREE.Mesh;
  private readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly slots: THREE.Vector2[] = Array.from({ length: 6 }, () => new THREE.Vector2());
  private readonly slotNode: N;
  private compiled: Promise<void> | null = null;
  private pipelineReady = false;
  private band = 0;
  private bandsDrawn = 0;

  constructor(
    private readonly renderer: THREE.WebGPURenderer,
    private readonly gen: TileGenerator,
    private readonly tree: TerrainQuadtree,
    readonly radius: number,
    profile: TerrainProfile,
    style: TerrainStyle,
    rig: LightRig,
    clouds: CloudLayer | null,
  ) {
    this.target = new THREE.RenderTarget(SIZE, SIZE, {
      type: THREE.HalfFloatType,
      depthBuffer: false,
      generateMipmaps: true,
      minFilter: THREE.LinearMipmapLinearFilter,
      magFilter: THREE.LinearFilter,
    });
    this.target.texture.name = 'world panorama';
    this.slotNode = uniformArray(this.slots, 'vec2');
    const material = new THREE.MeshBasicNodeMaterial();
    material.vertexNode = vec4(positionGeometry.xy, 0, 1);
    material.fragmentNode = this.build(profile, style, rig, clouds);
    material.depthTest = false;
    material.depthWrite = false;
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
    this.mesh.frustumCulled = false;
  }

  /** Compile the pass in the background (once). */
  precompile(): Promise<void> {
    this.compiled ??= this.renderer.compileAsync(this.mesh, this.camera).then(() => {
      this.pipelineReady = true;
    });
    return this.compiled;
  }

  /** Redraw the map (needs the six root tiles; skipped until they are all loaded). */
  update(dt: number): void {
    this.time.value += dt;
    for (const root of this.tree.roots) {
      if (root.state !== 'ready' || root.slot < 0) return;
      this.slots[root.face].set(this.gen.slotX(root.slot), this.gen.slotY(root.slot));
    }
    // Until the pass is compiled a draw would be skipped (AsyncPipelines).
    if (!this.pipelineReady) {
      void this.precompile();
      return;
    }
    // One band of the map a frame (the light and the clouds change slowly): a quarter of the
    // cost. It must not clear the rest, which a clearing pass would, scissor or not.
    const r = this.renderer;
    const prevTarget = r.getRenderTarget();
    const prevClear = r.autoClear;
    const prevScissor = r.getScissorTest();
    const h = SIZE / BANDS;
    this.target.scissor.set(0, this.band * h, SIZE, h);
    r.autoClear = false;
    r.setScissorTest(true);
    r.setRenderTarget(this.target);
    // The mip chain is rebuilt once the last band is in (three does it after each draw while
    // the flag is set). The flag stays set otherwise: the texture keeps its mip levels.
    const tex = this.target.texture;
    tex.generateMipmaps = this.band === BANDS - 1;
    r.render(this.mesh, this.camera);
    tex.generateMipmaps = true;
    r.setRenderTarget(prevTarget);
    r.setScissorTest(prevScissor);
    r.autoClear = prevClear;
    this.band = (this.band + 1) % BANDS;
    if (++this.bandsDrawn >= BANDS) this.ready.value = 1;
  }

  /**
   * The light the wall gives off (luminance) along hollow-local unit direction `dir`, blurred to
   * mip `level` (the map keeps them all, down to the whole wall's mean).
   */
  emissionAt(dir: N, level: number): N {
    return texture(this.target.texture, octEncode(dir)).level(float(level)).a;
  }

  /** The wall's radiance along hollow-local unit direction `dir`, blurred to mip `level`. */
  sample(dir: N, level: N | number): N {
    return texture(this.target.texture, octEncode(dir)).level(clamp(level, 0, LEVELS - 1)).rgb;
  }

  private build(profile: TerrainProfile, style: TerrainStyle, rig: LightRig, clouds: CloudLayer | null): N {
    const waterSky = this.waterSky;
    const time = this.time;
    const gen = this.gen;
    const R = this.radius;
    const axes: N = uniformArray(
      FACE_N.flatMap((n, f) => [n.clone(), FACE_U[f].clone(), FACE_V[f].clone()]),
      'vec3',
    );
    const slots = this.slotNode;
    /** Bilinear fetch of a root tile at grid coordinate g ∈ [0,1]². */
    const sampleTile = (tex: N, slot: N, g: N): N => {
      const t = g.mul(TILE_RES - 1);
      const t0 = clamp(floor(t), vec2(0), vec2(TILE_RES - 2));
      const f = t.sub(t0);
      const base = slot.add(t0);
      const a = textureLoad(tex, ivec2(base));
      const b = textureLoad(tex, ivec2(base.add(vec2(1, 0))));
      const c = textureLoad(tex, ivec2(base.add(vec2(0, 1))));
      const d = textureLoad(tex, ivec2(base.add(vec2(1, 1))));
      return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
    };
    return Fn(() => {
      const d: N = octDecode(screenUV);
      // The cube face it falls on (as cubeSphere's dirToFace), and where on it.
      const ad: N = abs(d);
      const face = select(
        ad.x.greaterThanEqual(ad.y).and(ad.x.greaterThanEqual(ad.z)),
        select(d.x.greaterThanEqual(0), float(0), float(1)),
        select(
          ad.y.greaterThanEqual(ad.z),
          select(d.y.greaterThanEqual(0), float(2), float(3)),
          select(d.z.greaterThanEqual(0), float(4), float(5)),
        ),
      );
      const fi = int(face.add(0.5));
      const Nf = axes.element(fi.mul(3));
      const Uf = axes.element(fi.mul(3).add(1));
      const Vf = axes.element(fi.mul(3).add(2));
      const q = d.div(dot(d, Nf));
      const g = vec2(atan(dot(q, Uf)), atan(dot(q, Vf)))
        .mul(4 / Math.PI)
        .mul(0.5)
        .add(0.5);
      const slot = slots.element(fi);
      const tile = sampleTile(gen.atlas, slot, g);
      // The surface normal from the tile's gradient (as TerrainRenderer's).
      const gU = tile.z;
      const gV = tile.w;
      const gN = gU
        .mul(dot(d, Uf))
        .add(gV.mul(dot(d, Vf)))
        .negate()
        .div(dot(d, Nf));
      const up = d.negate();
      const n = normalize(up.sub(Uf.mul(gU).add(Vf.mul(gV)).add(Nf.mul(gN))));
      const hRaw = tile.x;
      const p = d.mul(float(R).sub(max(hRaw, 0)));
      // A texel spans about R·√(4π)/SIZE of ground: finer noise fades to its mean.
      const footprint = float((R * Math.sqrt(4 * Math.PI)) / SIZE);
      const ctx: SurfaceContext = {
        p,
        h: paletteHeight(profile, hRaw),
        slope: float(1).sub(dot(n, up)),
        up,
        dist: float(R),
        near: float(0),
        mid: float(0),
        footprint,
        biome: gen.biomeAtlas ? sampleTile(gen.biomeAtlas, slot, g) : undefined,
        ridge: gen.surfaceAtlas ? sampleTile(gen.surfaceAtlas, slot, g).xyz.mul(2).sub(1) : undefined,
        profile,
        time,
      };
      const land = style.land(ctx);
      const light = rig.daylightAt(p, n);
      let ground: N = land.albedo.mul(light).mul(1 / Math.PI);
      if (land.emissive) ground = ground.add(land.emissive);
      const luminance = (c: N): N => dot(c, vec3(0.2126, 0.7152, 0.0722));
      // Water, seen from straight above: its deep colour, and a little of the sky it reflects.
      const ws = style.water;
      const wet = float(1).sub(clamp(hRaw.mul(4).add(1), 0, 1));
      let sea: N = vec3(...ws.deep)
        .mul(rig.daylightAt(d.mul(R), up))
        .mul(0.98 / Math.PI)
        .add(vec3(waterSky as N).mul(0.02));
      const seaGlow = ws.emissive ? ws.emissive(ctx) : null;
      if (seaGlow) sea = sea.add(seaGlow);
      let radiance: N = mix(ground, sea, wet);
      let emission: N = mix(
        land.emissive ? luminance(land.emissive) : float(0),
        seaGlow ? luminance(seaGlow) : float(0),
        wet,
      );
      if (clouds) {
        // The cloud deck from above: sunlit tops, brightest where the cloud is thickest (as
        // CloudLayer draws the far side's clouds).
        const Rc = clouds.radius;
        const f = clouds.fieldAt(d, float((Rc * Math.sqrt(4 * Math.PI)) / SIZE));
        const sun = rig.lightColorAt(d.mul(Rc - 50)).mul(rig.sunIrradiance * (R / Rc) ** 2);
        const ambient = vec3(rig.ambientColor as N).mul(rig.veilAt(d).mul(0.7).add(0.3));
        const tops = sun
          .mul(f.thick.mul(0.3).add(0.7))
          .add(ambient.mul(0.3))
          .mul(0.9 / Math.PI);
        radiance = mix(radiance, tops, f.alpha);
        emission = emission.mul(float(1).sub(f.alpha));
      }
      return vec4(radiance, emission);
    })();
  }
}
